/**
 * Envío AP-11 · «Capturar con cámara»: reglas puras (sin DOM) para la
 * captura de foto y video, el recorte y el destino en el Canvas.
 */

export const DURATIONS = [5, 10, 15, 20, 30];
export const DEFAULT_DURATION = 15;
export const COUNTDOWN_SECONDS = 3;
export const MIN_CLIP = 1;
const DUR_KEY = 'sml.camera.maxSec';

/**
 * A qué nodo se conecta lo capturado. `min`/`max`: lo que acepta el modelo
 * como VIDEO DE ENTRADA (no la duración de su salida):
 *  · Dream Actor (bytedance/dreamactor-m2.0): 3–30 s, hasta 30 MB.
 *  · Video con sonido en modo editar (xai/grok-imagine-video): hasta 8,7 s.
 *  · LipSync (sync/lipsync-2): minutos; acá manda el tope de la cámara.
 */
export const VIDEO_DESTINATIONS = [
  { key: 'dreamActor', label: 'Dream Actor', type: 'dreamActor', handle: 'inputVideo', min: 3, max: 30, data: {},
    hint: 'Copia tu movimiento en un personaje' },
  { key: 'videoWithSound', label: 'Video con sonido', type: 'videoWithSound', handle: 'videoInput', min: 1, max: 8.7,
    data: { mode: 'video-edit' }, hint: 'Edita el video con un pedido' },
  { key: 'lipSync', label: 'LipSync', type: 'lipSync', handle: 'inputVideo', min: 1, max: null, data: {},
    hint: 'Sincroniza los labios con un audio' },
  { key: 'none', label: 'Sólo cargar en el nodo', type: null },
];

export const PHOTO_DESTINATIONS = [
  { key: 'none', label: 'Sólo cargar en el nodo', type: null },
  { key: 'banana2pro', label: 'Banana 2 Pro', type: 'banana2pro', handle: 'multiInput', data: { prompt: 'Usá esta imagen como referencia.' },
    hint: 'Editala o usala de referencia' },
  { key: 'dreamActor', label: 'Dream Actor (personaje)', type: 'dreamActor', handle: 'inputImage', data: {},
    hint: 'Animá esta foto con un movimiento' },
];

export const DEFAULT_VIDEO_DEST = 'dreamActor';
export const DEFAULT_PHOTO_DEST = 'none';

export const destinationFor = (kind, key) => (kind === 'video' ? VIDEO_DESTINATIONS : PHOTO_DESTINATIONS)
  .find((d) => d.key === key) || null;

/** «7,2 s» */
export function fmtSec(s) {
  const v = Math.max(0, Number(s) || 0);
  const r = Math.round(v * 10) / 10;
  return `${String(r).replace('.', ',')} s`;
}

/** «00:07» */
export function fmtClock(s) {
  const v = Math.max(0, Math.floor(Number(s) || 0));
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
}

export function loadDuration(storage = safeStorage()) {
  try {
    const v = Number(storage?.getItem(DUR_KEY));
    return DURATIONS.includes(v) ? v : DEFAULT_DURATION;
  } catch (_e) { return DEFAULT_DURATION; }
}

export function saveDuration(v, storage = safeStorage()) {
  try { if (DURATIONS.includes(v)) storage?.setItem(DUR_KEY, String(v)); } catch (_e) { /* sin storage */ }
}

function safeStorage() {
  try { return window.localStorage; } catch (_e) { return null; }
}

/**
 * Normaliza el tramo [start, end] dentro de [0, total], con al menos MIN_CLIP
 * y, si el destino tiene tope, sin pasarlo (achica desde el final).
 */
export function clampTrim(start, end, total, dest = null) {
  const T = Math.max(0, Number(total) || 0);
  let s = Math.min(Math.max(0, Number(start) || 0), Math.max(0, T - MIN_CLIP));
  let e = Math.min(Math.max(Number(end) || 0, s + MIN_CLIP), T);
  if (e - s < MIN_CLIP) s = Math.max(0, e - MIN_CLIP);
  const max = dest?.max;
  if (max && e - s > max) e = s + max;
  return { start: round(s), end: round(e) };
}

const round = (v) => Math.round(v * 100) / 100;

/** Qué impide usar el tramo con ese destino (o null). */
export function trimProblem(start, end, dest) {
  const len = end - start;
  if (len < MIN_CLIP) return 'El tramo tiene que durar al menos 1 segundo.';
  if (dest?.min && len < dest.min - 1e-6) return `${dest.label} necesita al menos ${fmtSec(dest.min)}.`;
  if (dest?.max && len > dest.max + 1e-6) return `${dest.label} acepta hasta ${fmtSec(dest.max)}.`;
  return null;
}

/** El primer formato que el navegador sabe grabar (MP4 si puede: menos conversión). */
export function pickRecorderMime(isSupported) {
  const order = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  for (const m of order) {
    try { if (isSupported(m)) return m; } catch (_e) { /* sigue */ }
  }
  return '';
}

export const extForMime = (mime) => (String(mime).includes('mp4') ? '.mp4' : '.webm');

/** Error de getUserMedia → caso de la pantalla «sin cámara». */
export function cameraErrorCase(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return 'denied';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'nocam';
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'busy';
  if (name === 'Unsupported') return 'unsupported';
  return 'other';
}

export const ERROR_TEXT = {
  denied: ['El navegador no dio permiso a la cámara',
    'Para capturar, Studio Media Labs necesita usar tu cámara. Sólo se enciende mientras esta ventana está abierta.'],
  nocam: ['No encontramos una cámara', 'Conectá una cámara o probá desde el celular. Mientras tanto, podés subir un archivo.'],
  busy: ['La cámara está en uso', 'Otra aplicación (Zoom, Meet, Teams…) la está usando. Cerrala y probá de nuevo.'],
  unsupported: ['Este navegador no permite usar la cámara', 'Probá con Chrome, Edge o Safari actualizados, desde una dirección https.'],
  other: ['No se pudo abrir la cámara', 'Probá de nuevo. Si sigue igual, recargá la página.'],
};

/** Resolución pedida a la cámara. */
export const QUALITY = {
  '720p': { width: 1280, height: 720, bitrate: 2500000 },
  '1080p': { width: 1920, height: 1080, bitrate: 4000000 },
};

/** Nombre del archivo: «camara-2026-10-27-1834». */
export function captureName(kind, d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return `${kind === 'video' ? 'camara-video' : 'camara-foto'}-${stamp}`;
}
